import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetDataUsageQuery } from "../../../api/network/vmApiService";
import { CameraDetail, HealthTimes } from "../../../api/types/CamerasAdminVM";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { useAccountTimezone } from "../../../hooks/timezone";
import { APP_CONFIG } from "../../../constants/constants";

interface ScreenCameraAppProps {
  dataDeviceDetails: CameraDetail | null;
  healthTimesStamp: HealthTimes | null;
}

const ScreenCameraApp: FC<ScreenCameraAppProps> = ({
  dataDeviceDetails,
  healthTimesStamp,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras.camera_details.tabs.app",
  });

  const {
    serial_number,
    first_connected_timestamp,
    last_connected_timestamp,
    latest_timestamp,
  } = dataDeviceDetails || {};

  const {
    latest_app_launch_timestamp,
    latest_app_crash_timestamp,
    latest_upload_data_failure_timestamp,
  } = healthTimesStamp || {};

  const from = useMemo(() => Date.now() - 1000 * 60 * 60 * 24 * 30, []);
  const to = useMemo(() => Date.now(), []);
  const { tzForDateFns } = useAccountTimezone();
  const {
    data: dataUsageResponse,
    isLoading: isLoadingDataUsage,
    error,
  } = useGetDataUsageQuery(
    {
      sn: serial_number ?? "",
      from,
      to,
      frequency: "day",
      separate: true,
    },
    {
      skip: !serial_number,
    }
  );

  const rawData = dataUsageResponse?.data?.data?.usage ?? [];

  const LeftInfoItems = [
    {
      label: t("first_connected_timestamp"),
      value: first_connected_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(first_connected_timestamp),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    {
      label: t("last_app_launch_timestamp"),
      value: latest_app_launch_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(latest_app_launch_timestamp?.status),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    {
      label: t("last_app_crash_timestamp"),
      value: latest_app_crash_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(latest_app_crash_timestamp?.status),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
  ];

  const RightInfoItems = [
    {
      label: t("last_connected_timestamp"),
      value: last_connected_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(last_connected_timestamp),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    {
      label: t("last_data_upload_failure"),
      value: latest_upload_data_failure_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(latest_upload_data_failure_timestamp?.status),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
  ];

  const chartData = rawData
    .map((item) => {
      const date = new Date(item.from).toISOString().split("T")[0];
      const lteDownload = +(
        (item.mobile.rx + item.wifi.rx) /
        (1024 * 1024)
      ).toFixed(2);
      const lteUpload = +(
        (item.mobile.tx + item.wifi.tx) /
        (1024 * 1024)
      ).toFixed(2);

      return {
        date,
        lteDownload,
        lteUpload,
      };
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return (
    <div className="col-span-9">
      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("heading")}
      </h1>
      <div className="bg-gray-100 w-full flex rounded-lg p-4 mt-2">
        <div className="w-1/2 px-4 pt-2 space-y-6">
          {LeftInfoItems.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span className="font-semibold text-sm">{item.label}</span>
              <span className="text-sm">{item.value}</span>
            </div>
          ))}
        </div>
        <div className="border-r border-dotted border-gray-400 h-auto mx-2" />
        <div className="w-1/2 px-4 pt-2 space-y-4">
          {RightInfoItems.map((item, idx) => (
            <div key={idx} className="flex flex-col gap-2">
              <span className="font-semibold text-sm">{item.label}</span>
              <span className="text-sm">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
      <h1 className="font-semibold text-base leading-none align-middle col-span-9 mt-4">
        {t("data_usage")}
      </h1>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" />
          <YAxis
            label={{
              value: "Usage in MB",
              angle: -90,
              position: "insideLeft",
              offset: -5,
              style: { textAnchor: "middle" },
            }}
          />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="lteDownload"
            stroke="#0D82F0"
            dot={false}
            name={t("lte_download")}
          />
          <Line
            type="monotone"
            dataKey="lteUpload"
            stroke="#FF928A"
            dot={false}
            name={t("lte_upload")}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScreenCameraApp;
